// File: src/context/AuthContext.js

import React, {
  createContext,
  useState,
  useEffect,
} from 'react';

import axios from '../utils/axiosGlobal';
import ModerationBottomSheet from '../components/ModerationBottomSheet';

export const AuthContext = createContext();

export function handleLogout() {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('sessionId');

  localStorage.setItem(
    'logoutReason',
    'Session expired - please log in again!'
  );

  window.location.href = '/login';
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);

  const [warningDismissed, setWarningDismissed] = useState(
    () => localStorage.getItem('warningDismissed') === 'true'
  );

  const [isLoadingUser, setIsLoadingUser] = useState(false);

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');

    if (storedUserId) {
      fetchUserDoc(storedUserId);
    }

    const intervalId = setInterval(() => {
      const token = localStorage.getItem('accessToken');

      if (token) {
        axios.get('/auth/check').catch((err) => {
          console.warn('Auth heartbeat error:', err);
        });
      }
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleStorageEvent = (event) => {
      if (event.key === 'userId') {
        const newUserId = localStorage.getItem('userId');

        if (
          currentUser?._id &&
          (
            !newUserId ||
            newUserId !== String(currentUser._id)
          )
        ) {
          handleLogout();
        }
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    return () => {
      window.removeEventListener(
        'storage',
        handleStorageEvent
      );
    };
  }, [currentUser]);

  async function fetchUserDoc(userId) {
    setIsLoadingUser(true);

    try {
      const userRes = await axios.get(`/users/${userId}`);
      const modRes = await axios.get('/auth/me', {
        params: { userId },
      });

      let isHost = false;

      if (userRes.data?.email) {
        try {
          const hostRes = await axios.get(
            '/promoters/is-host',
            {
              params: {
                email: userRes.data.email,
              },
            }
          );

          isHost = !!hostRes.data?.isHost;
        } catch (err) {
          console.warn('Host check failed:', err);
        }
      }

      const mergedUser = {
        ...userRes.data,
        ...modRes.data,

        // The user document owns the saved market preference.
        selectedMarketId:
          userRes.data?.selectedMarketId ?? null,

        isHost,
      };

      setCurrentUser(mergedUser);
    } catch (err) {
      console.error('Error fetching user doc:', err);
    } finally {
      setIsLoadingUser(false);
    }
  }

  function handleLoginSuccess(userId) {
    const normalizedUserId = String(userId);
    const existingId = localStorage.getItem('userId');

    if (
      existingId &&
      existingId !== normalizedUserId
    ) {
      localStorage.clear();
    }

    localStorage.setItem('userId', normalizedUserId);
    fetchUserDoc(normalizedUserId);
  }

  let sheetToShow = null;

  if (currentUser?.isBanned) {
    sheetToShow = 'ban';
  } else if (currentUser?.isTempBanned) {
    sheetToShow = 'tempBan';
  } else if (
    currentUser?.hasWarning &&
    !warningDismissed
  ) {
    sheetToShow = 'warning';
  }

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        isLoadingUser,
        handleLoginSuccess,
        handleLogout,
      }}
    >
      {children}

      {sheetToShow === 'ban' && (
        <ModerationBottomSheet
          isOpen
          type="ban"
          reason={currentUser.banReason}
          reportNumber={currentUser.banReportId}
          onClose={handleLogout}
        />
      )}

      {sheetToShow === 'tempBan' && (
        <ModerationBottomSheet
          isOpen
          type="tempBan"
          reason={currentUser.tempBanReason}
          reportNumber={currentUser.tempBanReportId}
          banExpiresAt={currentUser.tempBanExpiresAt}
          onClose={handleLogout}
        />
      )}

      {sheetToShow === 'warning' && (
        <ModerationBottomSheet
          isOpen
          type="warning"
          reason={currentUser.warningMessage}
          onClose={() => {
            setCurrentUser((previous) =>
              previous
                ? {
                    ...previous,
                    hasWarning: false,
                    warningMessage: '',
                  }
                : previous
            );

            setWarningDismissed(true);

            localStorage.setItem(
              'warningDismissed',
              'true'
            );
          }}
        />
      )}

      {isLoadingUser && (
        <div style={styles.loading}>
          <em>Loading user info...</em>
        </div>
      )}
    </AuthContext.Provider>
  );
}

const styles = {
  loading: {
    position: 'fixed',
    top: 10,
    right: 10,
    background: '#fff',
    border: '1px solid #ccc',
    padding: '8px',
    borderRadius: '4px',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  },
};